/*
 * improvemint-delivery.js — Best Buy home-delivery date picker on the PDP.
 *
 * The sibling of improvemint-pickup.js. That file owns Store Pickup and Ground
 * Shipping; this one owns the third card, Home Delivery, and the drawer that
 * picks a date and window for it. They share one radio group, one ZIP and one
 * cart, and the seams between them are listed under "Shared with
 * improvemint-pickup.js" below.
 *
 * Contracts (docs/COMMERCE-PDP-PICKER.md §6 and §9 are the locked design):
 *
 *   Endpoint   GET /apps/bestbuy/delivery?sku=<n>&zip=<5 digits>
 *              → 200 { sku, zip, env, windows: [{date, startTime, endTime,
 *                      label}], eligibleWindows, earliest, leadTimeDays,
 *                      earliestOfferableDate, deliveryServiceIds, cachedAt }
 *              → 4xx/5xx { error: invalid-zip | invalid-sku | invalid-services
 *                          | not-home-delivery | disabled | busy | unavailable }
 *              App-proxy authenticated, same as /apps/bestbuy/options.
 *              `earliest` decides "as soon as", and `earliest: null` means no
 *              date — see earliestWindow(). Only `eligibleWindows` is ever
 *              offered or booked, never raw `windows`, which include days our
 *              home-delivery lead time forbids — see offerableWindows()
 *              (2026-10-17).
 *
 *   Markup     [data-im-fulfillment]                        the PDP fieldset
 *              [data-im-fulfillment-option="home-delivery"] this card, rendered
 *                                                           `hidden` by Liquid
 *              [data-im-home-delivery-state]                the card's one line,
 *                                                           written here
 *              [data-im-delivery-zip]                       the LINK inside that
 *                                                           line — the date, or
 *                                                           the ZIP prompt
 *                                                           (2026-10-19). Opens
 *                                                           the drawer
 *              #im-delivery-modal                           the drawer itself;
 *                                                           `data-mode` = zip |
 *                                                           dates (2026-10-19)
 *              [data-im-delivery-note]                      in the drawer, but
 *                                                           written by
 *                                                           improvemint-pickup.js
 *                                                           (it holds the options
 *                                                           answer; `note` never
 *                                                           reaches this file)
 *              [data-im-cart-delivery-earliest]             on the fieldset: "1"
 *                                                           when the window the
 *                                                           cart holds is the
 *                                                           earliest we were
 *                                                           offered, so the
 *                                                           server-rendered card
 *                                                           and this file word it
 *                                                           the same way
 *
 *   Events     im:home-delivery-offer  heard on `document`, from
 *                                      improvemint-pickup.js:
 *                                      `{ offered: bool, earliestDate: string }`.
 *                                      `offered` is the only thing that unhides
 *                                      the card. `earliestDate` is "" until the
 *                                      options answer carries one, and is a LINE
 *                                      only — a date with no start time is not a
 *                                      placeable choice. Not dispatched for an
 *                                      answer carrying `error` (2026-10-02), so
 *                                      a Best Buy outage leaves this card as it
 *                                      stands.
 *              im:home-delivery-card   dispatched on `document` by this file,
 *                                      `{ shown: bool }`, whenever the card is
 *                                      shown or hidden — with
 *                                      `data-im-home-delivery-shown` on the
 *                                      fieldset — because improvemint-pickup.js
 *                                      blocks Add to Cart when no option is left
 *                                      on the page (announceCardShown).
 *
 *   Cart       cart attributes, never line-item properties. One fulfillment per
 *              cart, last choice wins.
 *
 * ── "AS SOON AS", AND NO FORCED DATE (2026-09-25) ────────────────────────
 * GP, after comparing this card with bestbuy.com's: *"On best buy i see it checks
 * zip code first, same as pickup in store, then it gives an estimate in the
 * delivery card and inside cart/minicart like 'As soon as Sat, Sep 26'. I don't
 * have to choose a date for best buy, do we need to force to choose a date on our
 * website? can we just show the earliest date available and say 'as soon as' so
 * its not exact."*
 *
 * So selecting Home Delivery no longer requires the drawer. Its radio is
 * selectable as soon as the card is offered, and choosing it books the EARLIEST
 * window we were offered for this SKU and ZIP — `bb_delivery_earliest: "1"` — so
 * the cart still carries a COMPLETE window (the backend fails an incomplete one,
 * DELIVERY_WINDOW_MISSING) while the shopper was asked for nothing but a ZIP.
 * The card, the cart line and the cart banner then read "As soon as Sat, Sep 26":
 * the date, worded as the estimate it has always been, with no clock time.
 *
 * The drawer is KEPT and reachable from the card's date link, because some
 * shoppers do want a specific day. A window picked there is NOT the earliest by
 * assumption, so it is written with `bb_delivery_earliest: ""` and every surface
 * shows the day and its part of day instead (the exact clock window until
 * 2026-10-19, below) — "as soon as next Friday" would be a lie about a date the
 * shopper chose for their own reasons.
 *
 * THE DEFECT THIS ALSO FIXES. The radio used to be disabled until a window was
 * picked, and on a delivery-only appliance with no pickup the middle card is
 * hidden and disabled (improvemint-pickup.js, §3a) — so the shopper could be left
 * with NOTHING selected in the group. ensureSelection() can now land on this
 * card, and does. (With this card NOT offered as well — the owner switch off —
 * nothing is left, and since 2026-10-02 the page says so and blocks the add.)
 *
 * ── THE DATE IS THE LINK; MORNING / AFTERNOON (2026-10-19) ───────────────
 * GP: *"for a sku with home delivery, it asked me to choose a date to delivery.
 * By default if we don't have a zip, ask for zip, then lets auto-pick the earliest
 * date + the configurable lead days … once we have the date populated in the
 * card, we can make that date a link so if user clicks it they can choose a
 * different date (similar to how we show the store in store pickup card …). Lets
 * group times to morning or afternoon, and only show that options per day."*
 *
 * What he saw, read off the live PDP of the Fisher & Paykel dishwasher (sku
 * 6196700) on 2026-10-19: with no ZIP the card read "Enter a ZIP for the earliest
 * date" over an "Enter ZIP" button that opened THIS drawer headed "Choose a
 * delivery date", carrying Best Buy's note "The date you choose is confirmed when
 * your order is placed", and the ZIP typed there produced a list of clock windows
 * to choose from. With ZIP 75034 it read "See the earliest date for 75034" over a
 * "choose date" button — the options answer for that SKU carried no
 * `earliestDate`, so there was no date to state.
 *
 * Now:
 *   - The card has ONE line and it is the link ([data-im-delivery-zip], inside
 *     [data-im-home-delivery-state]), styled as the pickup card's link. Its text
 *     is the date — "As soon as Thu, Oct 23", or "Thu, Oct 23 · Morning" for a
 *     drawer pick — once one is known; else the ZIP prompt, or "See the earliest
 *     date for 75034".
 *   - With no ZIP the link opens the drawer in ZIP MODE (`data-mode="zip"`): it
 *     asks for the ZIP and nothing else. The submit is the existing "the shopper
 *     submits a ZIP" lookup; it puts the earliest date on the card as the link
 *     and closes the drawer (peekEarliest). It books nothing — a ZIP is not a
 *     selection — unless a selection opened the drawer (drawerAutoPick), which
 *     books exactly as before.
 *   - Where the date comes from, cheapest first: a window in the cart; the date
 *     the last lookup this page made for this SKU + ZIP (`answered`, below —
 *     any of the existing call sites fills it, none is added); the options
 *     answer's `delivery.earliestDate`, which costs nothing. The lead time is in
 *     all three already (server-side); this file adds none.
 *   - The date link (and the card body, once the card is selected) opens the
 *     drawer in DATES MODE, which offers each day as at most three buttons,
 *     Morning, Afternoon or All day (dayPart). Choosing one books that group's
 *     EARLIEST-starting window, whole — placement needs a real window — with
 *     `bb_delivery_earliest: ""`, and every surface then reads "Thu, Oct 23 ·
 *     Morning" (windowLabelText here; improvemint-delivery-window-label.liquid
 *     for the card, cart line and cart banner).
 *   - No call site was added. On a page whose options answer carries no
 *     `earliestDate` and whose ZIP was given on an earlier page, the card reads
 *     "See the earliest date for 75034" until the shopper opens the drawer,
 *     selects the card or presses Add to Cart — stating a date before that would
 *     be a lookup on a page view (F5).
 *
 * ── CALL IT ONLY WHEN THE SHOPPER ASKS ───────────────────────────────────
 * Never on page load, never on a product card, never in a grid. This endpoint
 * spends the Commerce rate limit that live order placement shares (900/min,
 * F13), and Best Buy named "availability dates for every item each time an end
 * customer looks at their site" as misuse (Logan, F5). The triggers in this file
 * are, and are only: the shopper opens the drawer, the shopper submits a ZIP,
 * — since 2026-09-25 — the shopper SELECTS the Home Delivery radio, and — since
 * 2026-10-17 — the shopper presses Add to Cart with Home Delivery selected and no
 * window in the cart (see the fifth caller below). *Since 2026-10-28 that press
 * asks with a window in the cart too:* the add gate in improvemint-pickup.js
 * ("ZIP and method before the add") checks this SKU still has a date at this ZIP
 * before the line goes in, through eligibleWindows() below — the same press, the
 * same lookup and 60 s cache, so the booking that follows it costs nothing.
 *
 * That third one is new, and it clears the same bar as the other two: it is a
 * deliberate shopper act on this one product, at most one lookup per act, behind
 * the same 60 s cache per SKU+ZIP, and it is the request the shopper was about to
 * make anyway one click later by opening the drawer. It is emphatically NOT a page
 * view: a selection the SCRIPT makes (ensureSelection, offerCard, a repaint)
 * assigns `.checked` directly, which fires no `change` event, so no automatic
 * selection can ever reach it. That is load-bearing — auto-selecting this card on
 * ~7,600 appliance pages is exactly the per-view traffic F5 forbids.
 *
 * That is also why the card starts hidden and disabled rather than probing to
 * find out whether it should exist — the gate below reads flags that arrived on
 * a fetch somebody else was already making.
 *
 * A FIFTH, AND THE ONE THAT CLOSED A PLACEMENT BUG (2026-10-17): the shopper
 * presses ADD TO CART with Home Delivery selected and no window in the cart.
 * ensureSelection() checks this card on every delivery-only appliance with no
 * pickup, and that fires no `change` — so a shopper who never touched the card
 * had a selected Home Delivery and no window, and improvemint-pickup.js's add
 * re-assertion used to CLEAR the fulfilment rather than look one up. The cart
 * then carried only `bb_zip`, checkout offered "Standard — FREE", and placement
 * failed: ship-to-home for a truck-only SKU (reproduced on the live theme on
 * every delivery-only appliance, Fisher & Paykel dishwasher sku 6196700). The
 * add now books the soonest window through bookSoonest() below before the line
 * goes in, or does not add at all. It clears the same bar as the radio: the
 * press is a deliberate act on this one product, it costs at most one lookup
 * behind the same 60 s cache, and it is the lookup selecting the card would have
 * made. Still never on a page view — the auto-selection itself asks nothing.
 *
 * A FOURTH CALLER, OUTSIDE THIS FILE (2026-10-01): the cart. GP: *"On add to cart
 * when we pull up minicart or when we visit the cart page, we should refresh home
 * delivery with new date."* A cart booked days ago was still saying "Mon, Sep 28"
 * on Oct 1. improvemint-cart-fulfillment.js asks through soonestWindow() below
 * when a home-delivery cart's banner comes on screen (the drawer opens, the cart
 * page loads), and only when the window has passed or is an "as soon as" window
 * not yet re-read today (`bb_delivery_checked`) — so at most once a day per cart,
 * and never for a day the shopper picked that is still ahead. Opening your cart
 * is a deliberate act; a daily cap is what keeps it from being a per-view one.
 *
 * ── THE CARD IS HIDDEN UNTIL THE ANSWER OFFERS IT ────────────────────────
 * *Rewritten 2026-09-24. This section said the card "renders on every Best Buy
 * PDP in `unknown` with its radio disabled" because "/apps/bestbuy/options
 * conflates the two cases — its `delivery` object is `availableForShipping ||
 * availableForDelivery`". The first clause is no longer how it behaves and the
 * second has been false since #170 split the flags.*
 *
 * Liquid renders the card `hidden`. improvemint-pickup.js dispatches
 * `im:home-delivery-offer` from the /apps/bestbuy/options answer the PDP already
 * fetches on load, which carries `delivery.homeDelivery` and
 * `delivery.datePickerEnabled` per SKU, and offerCard() unhides this card only
 * when BOTH are true — Best Buy delivers the SKU, and a window can actually be
 * chosen. So no Best Buy call was added for the gate; the call sites listed above
 * are still the only ones, and all of them are shopper actions.
 *
 * Why it had to change: the card could only learn the owner switch was off by
 * ASKING, and it may only ask when the shopper asks — so with the switch off it
 * sat on every appliance page reading "Enter a ZIP to check dates" and did
 * nothing but disappear when pressed. GP measured that on the live preview of
 * `upcoming-2026-09-24`.
 *
 * Shipping is the fallback selection in every hidden state: Liquid checks it
 * unless the cart already holds a home-delivery window, and offerCard() /
 * hideCard() call ensureSelection() so a removal never empties the group while a
 * live option remains. *Corrected 2026-10-02:* this said "can never empty the
 * group". Since GP's "only show available shipping options",
 * improvemint-pickup.js hides the shipping card whenever a real answer says Best
 * Buy will not ship, so with this card withheld and no pickup the group IS empty
 * — and the page says the item is not available and blocks Add to Cart
 * (`no-option`). Both functions also report the card's visibility
 * (announceCardShown), because that decision depends on it.
 *
 * ── HOW IT STAYS INERT WHILE THE OWNER SWITCH IS OFF ─────────────────────
 * `CommerceSettings.homeDeliveryEnabled` is off by default and the endpoint then
 * answers 503 `{"error":"disabled"}` WITHOUT calling Best Buy. That answer is
 * the signal, and it is remembered in sessionStorage (HD_OFF_KEY), so the card
 * is removed on the first ask and never rendered again for the rest of the
 * session, on any product. Until someone asks, the card writes nothing, promises
 * nothing and leaves the server-rendered picker exactly as it was. There is no
 * second switch in the theme on purpose: a theme-side flag would be a second
 * source of truth for something the backend already answers, and the two would
 * drift the first time the owner flipped one of them.
 *
 * ── FAIL SAFE ────────────────────────────────────────────────────────────
 * Every failure leaves the server-rendered state standing and the radio
 * disabled. A date is only ever offered because Best Buy offered it in this
 * answer; nothing is inferred, guessed, extrapolated or carried over from a
 * previous ZIP. And a window is NOT held by Best Buy — it can be taken "within
 * even an hour" (F15) — so the drawer says the window is confirmed when the
 * order is placed, and placement re-reads it per SKU right before it claims the
 * order (DELIVERY_DATE_UNAVAILABLE if it has gone).
 *
 * ── SHARED WITH improvemint-pickup.js ────────────────────────────────────
 *   localStorage `im_bb_zip`  one ZIP for both drawers. Either drawer's form
 *                             writes it and then fires `im:zip-change` on
 *                             `document`; both files listen, and each ignores
 *                             the event it raised itself (detail.source).
 *   the radio group           both files listen for `change` on it. pickup.js
 *                             ignores the value "home-delivery"; this file
 *                             ignores the other two. Exactly one of them writes.
 *   the cart attribute set    both files POST ALL ELEVEN keys on every write, the
 *                             irrelevant ones as "" (which is how
 *                             /cart/update.js deletes an attribute) — and the
 *                             two display-only pickup keys since 2026-10-17,
 *                             which this file always writes "". Writing a
 *                             subset is what would leave a cart carrying half a
 *                             pickup beside half a delivery, and the backend
 *                             treats an incomplete home-delivery choice as a
 *                             failure (DELIVERY_WINDOW_MISSING) rather than
 *                             falling back to shipping — a delivery-only
 *                             appliance cannot ship.
 *
 * No dependencies. Idempotent.
 */
(function () {
  'use strict';
  if (window.__imDeliveryInit) return;
  window.__imDeliveryInit = true;

  var ENDPOINT = '/apps/bestbuy/delivery';
  var ZIP_KEY = 'im_bb_zip';                 // shared with improvemint-pickup.js
  var HD_OFF_KEY = 'im_bb_hd_off';           // the owner switch answered "disabled"
  var HD_NO_PREFIX = 'im_bb_hd_no:';         // this SKU is not home-delivery
  var CACHE_PREFIX = 'im_bb_d:';

  /*
   * Best Buy's ToS caps any cache of these windows at 60 s, and the server has
   * already spent part of that by the time we see the answer. So the browser
   * copy expires 60 s after `cachedAt` — Best Buy's clock, not ours — and never
   * outlives the server's own entry.
   */
  var MAX_CACHE_MS = 60 * 1000;
  var FALLBACK_CACHE_MS = 30 * 1000;         // cachedAt unparseable: assume half spent

  /* ---------------------------------------------------------------- storage */

  /* Every access is wrapped: Safari in private mode throws on write, and a
   * shopper with site data blocked must lose the cache, not the picker. */
  function read(store, key) {
    try { return store.getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { store.setItem(key, value); } catch (e) { /* non-fatal */ }
  }

  function getZip() {
    var z = read(localStorage, ZIP_KEY);
    return /^\d{5}$/.test(z || '') ? z : null;
  }
  function setZip(zip) { write(localStorage, ZIP_KEY, zip); }

  function switchedOff() { return read(sessionStorage, HD_OFF_KEY) === '1'; }
  function markSwitchedOff() { write(sessionStorage, HD_OFF_KEY, '1'); }

  function skuRefused(sku) { return read(sessionStorage, HD_NO_PREFIX + sku) === '1'; }
  function markSkuRefused(sku) { write(sessionStorage, HD_NO_PREFIX + sku, '1'); }

  function cacheGet(key) {
    try {
      var raw = JSON.parse(read(sessionStorage, CACHE_PREFIX + key) || 'null');
      if (!raw || Date.now() > raw.e) return null;
      return raw.v;
    } catch (e) { return null; }
  }
  function cacheSet(key, value) {
    var fetchedAt = Date.parse((value && value.cachedAt) || '');
    var expires = isFinite(fetchedAt)
      ? Math.min(fetchedAt + MAX_CACHE_MS, Date.now() + MAX_CACHE_MS)
      : Date.now() + FALLBACK_CACHE_MS;
    if (expires <= Date.now()) return;       // already stale on arrival
    write(sessionStorage, CACHE_PREFIX + key, JSON.stringify({ e: expires, v: value }));
  }

  /* ------------------------------------------------------------------ fetch */

  /*
   * Unlike the pickup endpoint's helper, this one reads the BODY of a failure
   * too: the whole design turns on telling `disabled` from `busy` from
   * `not-home-delivery`, and an `HTTP 503` on its own cannot. A response with no
   * readable JSON becomes `unavailable`, which is the safe reading of "we did
   * not get an answer".
   */
  var inFlight = {};

  /*
   * What the last answer for each SKU + ZIP said the earliest date is, for the
   * life of this page (2026-10-19). `{ date: "YYYY-MM-DD" }`, or `{ none: true }`
   * when the answer had no eligible window.
   *
   * It is what lets the card STATE the earliest date — GP's "auto-pick the
   * earliest date" — after any lookup the shopper already caused (opening the
   * drawer, submitting a ZIP, selecting the card, Add to Cart), when the options
   * answer carried no `earliestDate` to state it with. It is filled here, from
   * answers in hand, so it adds no call; and it is NOT a window cache — nothing
   * is booked from it. Booking always reads a fresh (≤ 60 s) answer.
   */
  var answered = {};

  function noteAnswer(key, data) {
    if (!data || data.error) return;
    var win = earliestWindow(data);
    answered[key] = win ? { date: win.date } : { none: true };
  }

  function lookup(sku, zip) {
    var key = sku + ':' + zip;
    return fetchWindows(key, sku, zip).then(function (data) { noteAnswer(key, data); return data; });
  }

  function fetchWindows(key, sku, zip) {
    var hit = cacheGet(key);
    if (hit) return Promise.resolve(hit);
    if (inFlight[key]) return inFlight[key];

    var url = ENDPOINT + '?sku=' + encodeURIComponent(sku) + '&zip=' + encodeURIComponent(zip);
    inFlight[key] = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json().catch(function () { return null; }).then(function (body) {
          if (r.ok && body && body.windows) {
            cacheSet(key, body);
            return body;
          }
          // Errors are NOT cached here. The server already caches its own
          // failures for 60 s per key, so a retry costs nothing upstream, and
          // caching "busy" in the browser would keep telling a shopper to wait
          // after the queue had cleared.
          return { error: (body && body.error) || 'unavailable' };
        });
      })
      .catch(function () { return { error: 'unavailable' }; })
      .then(function (out) { delete inFlight[key]; return out; });
    return inFlight[key];
  }

  /* ------------------------------------------------- the earliest window */

  var DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  var TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

  /*
   * The windows we may OFFER — never one inside the lead time (2026-10-17).
   *
   * The endpoint's `windows` is EVERY window Best Buy listed, including the ones
   * our home-delivery lead time forbids; `eligibleWindows` is the subset on or
   * after `earliestOfferableDate` (Best Buy's earliest window + the lead time,
   * never before today + it — delivery-dates.ts). Equal to `windows` at lead time
   * 0, so this changed nothing until a lead time was set. GP's rule: every date a
   * shopper sees is Best Buy's date plus our lead time, computed server-side.
   *
   * Until 2026-10-17 the drawer listed `windows`, so a shopper could pick — and
   * the cart carry — a day the lead time forbids; placement then advanced it or
   * failed it to a human (DELIVERY_DATE_UNAVAILABLE). docs/GAP-REGISTER.md → L1.
   *
   * An answer without `eligibleWindows` but with `earliestOfferableDate` (an
   * endpoint between 2026-09-25 and #293) is filtered by that floor here. An
   * answer with neither is not offered at all: a window we cannot check against
   * the lead time is not one we may put in front of a shopper. The deployed
   * endpoint always sends the field.
   */
  function offerableWindows(data) {
    if (!data) return [];
    if (Object.prototype.toString.call(data.eligibleWindows) === '[object Array]') return data.eligibleWindows;
    var floor = DATE_RE.test(data.earliestOfferableDate || '') ? data.earliestOfferableDate : '';
    if (!floor) return [];
    return (data.windows || []).filter(function (w) { return w && (w.date || '') >= floor; });
  }

  /*
   * The soonest window in an answer — the one "As soon as <date>" is about.
   *
   * THE ENDPOINT'S `earliest` DECIDES, INCLUDING WHEN IT IS NULL (2026-10-17).
   * /apps/bestbuy/delivery sends `earliest` — the earliest window at or after
   * Best Buy's earliest date + our home-delivery lead time, chosen by the same
   * function placement claims with (delivery-eta.ts → selectDeliveryWindow) — on
   * every answer, and `earliest: null` when there is no such window: Best Buy
   * offered none, or the lead time is past every window it did offer. Null means
   * "no date available", and this returns null for it — the callers then say
   * "No delivery dates in the next 14 days", the existing copy, and book nothing.
   *
   * *Until 2026-10-17 a null `earliest` fell through to a scan of the raw
   * `windows` array* — which, on exactly the "lead time past every window"
   * answer, picked a window the lead time forbids and booked it into the cart
   * (radio selection, Add to Cart's bookSoonest, the cart's daily re-read). That
   * fallback is gone.
   *
   * An answer with no `earliest` key at all is an endpoint older than #193. Only
   * then is a window derived, and from offerableWindows(), never raw `windows`:
   * `earliestDate` (a bare YYYY-MM-DD, matched back to its window) when sent,
   * else the soonest by date then start. The scan re-establishes that order
   * rather than trusting the array to be sorted, because an unsorted answer would
   * put a LATER date under the words "as soon as".
   */
  function earliestWindow(data) {
    if (!data) return null;

    if (Object.prototype.hasOwnProperty.call(data, 'earliest')) {
      var explicit = data.earliest;
      if (explicit && DATE_RE.test(explicit.date || '') && TIME_RE.test(explicit.startTime || '')) {
        return { date: explicit.date, startTime: explicit.startTime, endTime: explicit.endTime || '' };
      }
      // null, or a shape we cannot write whole: no date. Never a guess.
      return null;
    }

    var valid = offerableWindows(data).filter(function (w) {
      return w && DATE_RE.test(w.date || '') && TIME_RE.test(w.startTime || '');
    });
    if (!valid.length) return null;

    var pinned = typeof data.earliestDate === 'string' && DATE_RE.test(data.earliestDate)
      ? data.earliestDate
      : null;

    var best = null;
    for (var i = 0; i < valid.length; i++) {
      var w = valid[i];
      if (pinned && w.date !== pinned) continue;
      if (!best || w.date < best.date || (w.date === best.date && w.startTime < best.startTime)) best = w;
    }
    // An `earliestDate` naming a day with no offerable window is not a window
    // we can write, so take the soonest one that IS offerable rather than
    // writing half a choice.
    if (!best && pinned) return earliestWindow({ eligibleWindows: valid });
    if (!best) return null;
    return { date: best.date, startTime: best.startTime, endTime: best.endTime || '' };
  }

  /*
   * The soonest window for one SKU and ZIP, for the CART (2026-10-01).
   *
   * improvemint-cart-fulfillment.js re-reads a home-delivery cart's window when the
   * shopper opens the cart, at most once a day (see "a window that went stale"
   * there). It asks through here rather than keeping a copy of any of this, so the
   * cart and the PDP share one request shape, one 60 s cache, one memory of which
   * sessions and SKUs said no, and ONE rule for which window is the soonest —
   * earliestWindow() above, which takes the endpoint's own `earliest`. A second
   * copy is how "as soon as" would come to mean two different dates.
   *
   * Resolves `{ window, label }` or `{ error, asked }`; never rejects. `asked:
   * false` means no request was made (a refusal this session already heard), so
   * the caller's per-refresh cap counts only real asks. Refusals are recorded
   * exactly as handleError() records them, so a parcel SKU the cart asked about is
   * never asked about again this session from either side.
   */
  function soonestWindow(sku, zip) {
    if (!sku || !/^\d{5}$/.test(zip || '')) return Promise.resolve({ error: 'invalid-zip', asked: false });
    if (switchedOff()) return Promise.resolve({ error: 'disabled', asked: false });
    if (skuRefused(sku)) return Promise.resolve({ error: 'not-home-delivery', asked: false });
    return lookup(sku, zip).then(function (data) {
      if (data.error) {
        if (data.error === 'disabled') markSwitchedOff();
        if (data.error === 'not-home-delivery' || data.error === 'invalid-sku') markSkuRefused(sku);
        return { error: data.error };
      }
      var win = earliestWindow(data);
      return win ? { window: win, label: windowLabelText(win, true) } : { error: 'none' };
    });
  }

  // Published like improvemint-cart-fulfillment.js's switchToShipping: a promise
  // has to come back, which an event cannot give. This file loads first
  // (layout/theme.liquid), so the hook exists before the cart script runs.
  window.imDelivery = window.imDelivery || {};
  window.imDelivery.soonestWindow = soonestWindow;

  /*
   * How many windows we may offer for one SKU and ZIP, for the PDP's ADD GATE
   * (improvemint-pickup.js, "ZIP and method before the add" — GP, 2026-10-28).
   * His rule: a press of Add to Cart over Home Delivery is refused when
   * `eligibleWindows` is empty for that ZIP, and allowed otherwise. NOT the
   * options answer's `delivery.earliestDate`, which is proven wrong as a test:
   * sku 6196700 at 75034 has none and 11 eligible windows.
   *
   * The same lookup() as bookSoonest(), so one request shape, one 60 s cache and
   * one in-flight request: when the gate asks first, the booking that follows it
   * on the same press is a cache hit. It is that press's lookup, not a new kind of
   * call (the fifth caller in the header) — the one difference is that it is now
   * also made when the cart already holds a window, which was written for another
   * product or another day and says nothing about this SKU at this ZIP.
   *
   * Resolves `{ known: true, count }` or `{ known: false }`; never rejects. Not
   * known is never a no: an error, `busy`, a refused SKU, the switch off or no
   * ZIP lets the add go on, and bookSoonest() then does what it always did.
   * Refusals are recorded exactly as soonestWindow() records them.
   *
   * With no window for the SKU and ZIP still on the page, the card is repainted
   * from that answer (`unavailable`, "No delivery dates in the next 14 days" +
   * Change ZIP) — the state bookSoonest() left it in when it was the one that
   * found none, which the gate's refusal now reaches first.
   */
  function eligibleWindows(sku, zip) {
    if (!sku || !/^\d{5}$/.test(zip || '')) return Promise.resolve({ known: false });
    if (switchedOff() || skuRefused(sku)) return Promise.resolve({ known: false });
    return lookup(sku, zip).then(function (data) {
      if (!data || data.error) {
        if (data && data.error === 'disabled') markSwitchedOff();
        if (data && (data.error === 'not-home-delivery' || data.error === 'invalid-sku')) markSkuRefused(sku);
        return { known: false };
      }
      var count = offerableWindows(data).length;
      if (!count && currentSku() === sku && getZip() === zip) repaintCard();
      return { known: true, count: count };
    }, function () { return { known: false }; });
  }

  /*
   * The same question from answers already in hand, with NO request: the gate
   * re-reads its own verdict after a repaint, a ZIP change or a variant switch,
   * and none of those is a press. The browser cache (≤ 60 s) first; else what
   * this page's last lookup for the SKU and ZIP said (`answered`, which keeps
   * only whether a window existed — enough to tell "none").
   */
  function peekEligibleWindows(sku, zip) {
    if (!sku || !/^\d{5}$/.test(zip || '')) return { known: false };
    var key = sku + ':' + zip;
    var hit = cacheGet(key);
    if (hit && !hit.error) return { known: true, count: offerableWindows(hit).length };
    var seen = answered[key];
    if (seen) return { known: true, count: seen.none ? 0 : 1 };
    return { known: false };
  }

  /*
   * Ask for the ZIP the way a press of Add to Cart over Home Delivery always has
   * (bookSoonest's no-ZIP path): this drawer in ZIP mode, whose submit books the
   * soonest window. The add gate refuses a press with no ZIP before bookSoonest is
   * reached, and calls this so the Home Delivery shopper still gets the drawer.
   * No lookup. False when there is nothing to open it for.
   */
  function askZip() {
    var card = cardEl();
    if (!card || card.hidden || getZip()) return false;
    openDrawer(triggerEl(card) || card, true, 'zip');
    return true;
  }

  window.imDelivery.eligibleWindows = eligibleWindows;
  window.imDelivery.peekEligibleWindows = peekEligibleWindows;
  window.imDelivery.askZip = askZip;

  /* -------------------------------------------------------------- elements */

  function rootEl() { return document.querySelector('[data-im-fulfillment]'); }
  function cardEl() {
    var root = rootEl();
    return root && root.querySelector('[data-im-fulfillment-option="home-delivery"]');
  }
  function modalEl() { return document.getElementById('im-delivery-modal'); }

  /*
   * Read fresh every time on purpose — this is NOT constant for the life of the
   * page. improvemint-pickup.js rewrites `data-im-sku` when the shopper switches
   * variant (2026-09-25), because `bestbuy_sync.bb_sku` is product-level and
   * cannot say which of a consolidated product's SKUs is selected. Never cache it
   * in a closure: the per-SKU refusal flag and the window lookup are both keyed on
   * it, and a stale one would refuse or offer dates for the wrong appliance.
   */
  function currentSku() {
    var root = rootEl();
    return (root && root.getAttribute('data-im-sku')) || null;
  }

  function label(name, fallback) {
    var m = modalEl();
    return (m && m.getAttribute('data-label-' + name)) || fallback;
  }

  /* ------------------------------------------------------- the chosen window */

  /*
   * What the CART holds, read off the fieldset that Liquid rendered from
   * `cart.attributes`, and kept in step by every write below. Deliberately not
   * localStorage: the cart is the thing the order is built from, and a second
   * copy in localStorage would be free to disagree with it after a checkout, a
   * cart clear, or a second tab.
   *
   * A window counts only when it is COMPLETE. Anything less is not a choice the
   * backend would accept, so it is not one we display.
   */
  function cartWindow() {
    var root = rootEl();
    if (!root) return null;
    if (root.getAttribute('data-im-cart-fulfillment') !== 'home-delivery') return null;
    var date = root.getAttribute('data-im-cart-delivery-date') || '';
    var start = root.getAttribute('data-im-cart-delivery-start') || '';
    if (!date || !start) return null;
    return {
      date: date,
      startTime: start,
      endTime: root.getAttribute('data-im-cart-delivery-end') || '',
      zip: root.getAttribute('data-im-cart-delivery-zip') || '',
      // "We picked this one because it was the soonest" — the difference between
      // "As soon as Sat, Sep 26" and "Sat, Sep 26, 8:00 AM – 12:00 PM".
      earliest: root.getAttribute('data-im-cart-delivery-earliest') === '1'
    };
  }

  /*
   * Has the cart's window's DAY gone by? Today in UTC, compared as a string with
   * Best Buy's YYYY-MM-DD — the same day improvemint-cart-fulfillment.js
   * (windowNeedsReading), the checkout re-check and placement
   * (DELIVERY_DATE_PASSED) use, so the PDP cannot call a day current that the
   * cart and the order call passed.
   *
   * Display only (2026-10-29). GP found a PDP offering "Home Delivery · Fri, Oct
   * 23" six days after it: the cart kept an old window, and Liquid prints the
   * cart's window on every product page. A passed window is still the cart's
   * choice — the cart's own daily re-read replaces it when the drawer or the
   * cart page comes on screen, and placement never books before today — so this
   * only stops the PDP stating the date as if it were still the plan.
   */
  function windowPassed(win) {
    var n = new Date();
    function p(x) { return (x < 10 ? '0' : '') + x; }
    var today = n.getUTCFullYear() + '-' + p(n.getUTCMonth() + 1) + '-' + p(n.getUTCDate());
    return !!(win && win.date && win.date < today);
  }

  function rememberCart(fulfillment, win) {
    var root = rootEl();
    if (!root) return;
    root.setAttribute('data-im-cart-fulfillment', fulfillment);
    // Every write from this file deletes the pickup keys from the cart, so the
    // mirror forgets them too (the store id and, since 2026-10-17, the pickup
    // date improvemint-pickup.js compares before an add).
    root.setAttribute('data-im-cart-store-id', '');
    root.setAttribute('data-im-cart-pickup-ready-date', '');
    root.setAttribute('data-im-cart-delivery-date', (win && win.date) || '');
    root.setAttribute('data-im-cart-delivery-start', (win && win.startTime) || '');
    root.setAttribute('data-im-cart-delivery-end', (win && win.endTime) || '');
    root.setAttribute('data-im-cart-delivery-zip', (win && win.zip) || '');
    root.setAttribute('data-im-cart-delivery-earliest', (win && win.earliest) ? '1' : '');
  }

  /* ------------------------------------------------------------ cart writes */

  /*
   * ALL ELEVEN keys, every time — plus, since 2026-10-17, the two display-only
   * pickup keys (`bb_pickup_ready_date`, `bb_store_address`), always "" from
   * this file. "" deletes an attribute, so one write always
   * leaves the cart holding exactly one coherent fulfillment and no remnant of
   * the last one. Never call this with a subset.
   *
   * The eleventh is `bb_delivery_earliest` (2026-09-25). It exists because the
   * cart page and the mini-cart are Liquid, rendered from the attributes with no
   * script in the loop, and "did we pick this window or did the shopper" is not
   * derivable from a date and a time. PLACEMENT READS IT TOO: for "1",
   * deriveFulfillmentChoice (order-mapping.ts) keeps bb_delivery_date as the
   * quoted date — a floor, not a booking — and placement takes the first window on
   * or after it (never before today) from a fresh read; a drawer-picked window
   * ("") is booked exactly while Best Buy still offers it. Neither adds the lead
   * time again (bestbuy-sync, GP 2026-10-19). *Corrected 2026-10-17: this said it
   * was "DISPLAY ONLY: placement reads the ten named keys and ignores anything
   * else", untrue since 2026-10-01 (commit 93f95d7). Corrected 2026-10-19: it said
   * placement dropped the window and counted from intake day + the lead time.*
   */
  function updateCart(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () { /* non-fatal: re-asserted the next time the shopper picks */ });
  }

  /*
   * Tell the cart surfaces what was just written — see the twin in
   * improvemint-pickup.js for why. The mini-cart's Liquid was rendered before the
   * shopper opened this drawer, and a bare /cart/update.js does not re-render it.
   */
  function announceCart(state, value) {
    document.dispatchEvent(new CustomEvent('im:cart-fulfillment-change', {
      detail: { state: state, value: value || '' }
    }));
  }

  /*
   * `earliest` is TRUE when this window was chosen BY US because it was the
   * soonest on offer, and false when the shopper picked it out of the drawer. It
   * decides the wording everywhere downstream ("As soon as Sat, Sep 26" vs the
   * exact window), so it is passed explicitly at every call site rather than
   * inferred here — only the caller knows which of the two just happened.
   */
  function commitWindow(win, zip, earliest) {
    rememberCart('home-delivery', {
      date: win.date, startTime: win.startTime, endTime: win.endTime || '', zip: zip, earliest: !!earliest
    });
    announceCart('home-delivery', windowLabelText(win, !!earliest));
    return updateCart({
      bb_fulfillment: 'home-delivery',
      bb_zip: zip,
      bb_store_id: '',
      bb_store_name: '',
      bb_shipping_option: '',
      bb_delivery_date: win.date,
      bb_delivery_start: win.startTime,
      bb_delivery_end: win.endTime || '',
      bb_delivery_zip: zip,
      bb_delivery_earliest: earliest ? '1' : '',
      // Install / haul-away are not offered by this picker and placement
      // REFUSES an order that asks for them (DELIVERY_SERVICES_UNSUPPORTED,
      // BACKLOG S2/S3), so the windows are read without `&services=` and this
      // is written empty — which also clears anything a future build left here.
      bb_delivery_services: '',
      // The two display-only pickup keys (2026-10-17): a pickup's date and
      // address must not outlive the pickup.
      bb_pickup_ready_date: '',
      bb_store_address: ''
    });
  }

  /*
   * Give the cart back to Ground Shipping. Used whenever a home-delivery choice
   * stops being true — the ZIP changed, the switch turned out to be off, the SKU
   * turned out not to be deliverable. Never leaves `bb_fulfillment` set to
   * home-delivery with the window blanked: that is the half-filled cart the
   * backend fails on rather than ships.
   */
  function releaseToShipping() {
    rememberCart('delivery', null);
    announceCart('delivery', '');
    /*
     * A radio group with nothing checked is not a state the shopper can read.
     * Ground Shipping is where the cart now is, so it is where the selection
     * belongs — but on a delivery-only appliance that radio is disabled (§3a) and
     * there is nothing truthful to move to, so ensureSelection() puts the
     * selection on whatever control is still live rather than leave the group
     * empty. That can be this card again, with no window in the cart: the card
     * then says so, and the shopper's next act is to give us a date or a ZIP.
     */
    var root = rootEl();
    var ship = root && root.querySelector('[data-im-fulfillment-option="delivery"] input[type="radio"]');
    if (ship && !ship.disabled) ship.checked = true;
    else ensureSelection();
    return updateCart({
      bb_fulfillment: 'delivery',
      bb_zip: getZip() || '',
      bb_store_id: '',
      bb_store_name: '',
      bb_shipping_option: '',
      bb_delivery_date: '',
      bb_delivery_start: '',
      bb_delivery_end: '',
      bb_delivery_zip: '',
      bb_delivery_earliest: '',
      bb_delivery_services: '',
      bb_pickup_ready_date: '',
      bb_store_address: ''
    });
  }

  /* ---------------------------------------------------------------- painting */

  var CLOCK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var CLOCK_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /*
   * THE PART-OF-DAY RULE — the ONE copy in JS. Its Liquid twin is in
   * snippets/improvemint-delivery-window-label.liquid, under the same heading;
   * change both or neither, or the drawer and the cart will call one window two
   * different things.
   *
   *   'afternoon'  the window STARTS at or after 12:00
   *   'morning'    otherwise, the window ENDS at or before 13:00
   *   'allday'     anything else — it spans the middle of the day, or its end is
   *                unknown (Best Buy may send no end time)
   *
   * Afternoon is tested FIRST, and that only matters for a window lying wholly
   * inside 12:00–13:00, which both tests accept: it holds no morning minute, so
   * "Afternoon" is the true word for it and "Morning" would not be.
   *
   * Best Buy's own clock, exactly as the window gives it — no timezone
   * conversion, because the window is local to the delivery address, which is
   * the clock the shopper will be home by. '' for a start time that is not
   * "HH:MM", which no caller ever offers or prints.
   *
   * WHY THE END TIME COUNTS (corrected the same day, 2026-10-19). The first cut
   * read the START only — before 12:00 Morning, else Afternoon — and so called a
   * 07:00–19:00 window "Morning": a promise of a morning delivery for something
   * that may arrive at 7 PM. Every window the store's Commerce environment
   * (sandbox) returned was 07:00–19:00, so every day read "Morning". A window has
   * to END by 13:00 to be a morning, and one that does neither is "All day",
   * shown with its real hours (allDayHours), because "All day" alone hides the
   * 7 PM end.
   *
   * GP, 2026-10-19: "Lets group times to morning or afternoon, and only show that
   * options per day." *This file printed clock windows ("8:00 AM – 12:00 PM")
   * through clock12() until that day.*
   */
  function minutesOf(hhmm) {
    var m = TIME_RE.exec(hhmm || '');
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  }

  function dayPart(win) {
    var start = minutesOf(win && win.startTime);
    if (start === null) return '';
    if (start >= 12 * 60) return 'afternoon';
    var end = minutesOf(win.endTime);
    if (end !== null && end <= 13 * 60) return 'morning';
    return 'allday';
  }

  /* "07:00" -> "7 AM", "07:30" -> "7:30 AM", "12:00" -> "12 PM". The hours of an
   * "All day" window. Mirrors the clock arithmetic in
   * improvemint-delivery-window-label.liquid character for character. */
  function shortClock(hhmm) {
    var m = TIME_RE.exec(hhmm || '');
    if (!m) return '';
    var h = Number(m[1]);
    return (h % 12 === 0 ? 12 : h % 12) + (m[2] === '00' ? '' : ':' + m[2]) + ' ' + (h < 12 ? 'AM' : 'PM');
  }

  /*
   * The words for a window's part of the day — Theme settings → Improvemint
   * Financial — Home delivery → Morning / Afternoon / All day label. "All day"
   * carries its real hours, "All day (7 AM – 7 PM)", or none when Best Buy sent no
   * end time (an hour range with half of it missing would be worse than none).
   */
  function dayPartText(win) {
    var part = dayPart(win);
    if (part === 'morning') return label('morning', 'Morning');
    if (part === 'afternoon') return label('afternoon', 'Afternoon');
    if (part !== 'allday') return '';
    var allDay = label('all-day', 'All day');
    var end = shortClock(win.endTime);
    return end ? allDay + ' (' + shortClock(win.startTime) + ' – ' + end + ')' : allDay;
  }

  /* "2026-09-23" -> "Wed, Sep 23". Parsed as UTC and formatted from UTC parts:
   * `new Date('2026-09-23')` is midnight UTC, and reading it with local getters
   * west of Greenwich returns the 22nd. */
  function dayLabel(date) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
    if (!m) return date || '';
    var d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (isNaN(d.getTime())) return date;
    return CLOCK_DAYS[d.getUTCDay()] + ', ' + CLOCK_MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }

  /*
   * The one thing this file prints about a window, in both wordings:
   *
   *   earliest (we booked the soonest)  "As soon as Thu, Oct 23"
   *   picked in the drawer              "Thu, Oct 23 · Morning" / "· Afternoon" /
   *                                     "· All day (7 AM – 7 PM)"
   *
   * It is the JS twin of snippets/improvemint-delivery-window-label.liquid, which
   * renders the same two wordings from the same cart attributes on the PDP, the
   * cart line and the cart banner. The Liquid is the one that runs first and
   * survives a reload; this one exists because a PDP choice repaints the card and
   * the mini-cart without one. They must agree character for character, so any
   * change here is a change there. Only an "All day" pick prints a clock time,
   * and then the whole range (2026-10-19 — the drawer pick read "Thu, Oct 23,
   * 8:00 AM – 12:00 PM" here until then, while the cart's Liquid printed the bare
   * day). The "as soon as" wording names the day only: the date is the estimate,
   * and the window inside it is Best Buy's to move.
   */
  function windowLabelText(win, earliest) {
    if (earliest) return asSoonAsText(win.date);
    var part = dayPartText(win);
    return part ? dayLabel(win.date) + ' · ' + part : dayLabel(win.date);
  }

  function asSoonAsText(date) {
    return label('as-soon-as', 'As soon as') + ' ' + dayLabel(date);
  }

  /*
   * The earliest date the OPTIONS answer gave us, as of the last one we heard,
   * and the SKU + ZIP it was heard for. Written by the `im:home-delivery-offer`
   * listener below (and init(), off the fieldset), which fires after every options
   * fetch — page load, variant change, and every ZIP change in either drawer
   * (`im:zip-change` → loadPdp in improvemint-pickup.js). Read only while the SKU
   * and ZIP on the page still match (offeredDate), because the answer for a new
   * ZIP or variant lands a round trip after the change and the old one's date
   * must not stand on the card in between. *Until 2026-10-19 it was not keyed, so
   * that round trip could show the previous ZIP's "As soon as" date.* Empty when
   * the answer carried none (no ZIP, or Best Buy sent no dates).
   */
  var offeredEarliest = '';
  var offeredFor = '';

  function setOfferedDate(raw) {
    offeredEarliest = DATE_RE.test(raw || '') ? raw : '';
    offeredFor = (currentSku() || '') + ':' + (getZip() || '');
  }

  function offeredDate() {
    return offeredFor === (currentSku() || '') + ':' + (getZip() || '') ? offeredEarliest : '';
  }

  function triggerEl(card) {
    if (card && !card.__imTrigger) {
      var found = card.querySelector('[data-im-delivery-zip]');
      if (found) card.__imTrigger = found;
    }
    return (card && card.__imTrigger) || null;
  }

  /*
   * The card's link, as `{ text, aria }` — the one control in the card's line
   * (2026-10-19). `aria` is only set where the visible words do not say what
   * pressing it does.
   *
   *   dateLink      the date: "As soon as Thu, Oct 23" / "Thu, Oct 23 · Morning".
   *                 Spoken as "Change delivery date, currently <date>" (Theme
   *                 settings → Date link — screen-reader label).
   *   promptLink    no ZIP: "Enter a ZIP for the earliest date" — it says what it
   *                 does, so it is its own name.
   *   lookLink      a ZIP and no date yet: "See the earliest date for 75034".
   *   changeZipLink no dates for this ZIP: "Change ZIP" — the In-store pickup
   *                 group's own words for the same act, read off the fieldset
   *                 (`data-im-none-zip-change`) rather than copied.
   */
  function dateLink(text) {
    return { text: text, aria: label('change-date', 'Change delivery date, currently {date}').split('{date}').join(text) };
  }
  function promptLink() { return { text: label('prompt', 'Enter a ZIP for the earliest date') }; }
  function lookLink(zip) { return { text: label('zip-known', 'See the earliest date for') + ' ' + zip }; }
  function changeZipLink() {
    var root = rootEl();
    return { text: (root && root.getAttribute('data-im-none-zip-change')) || 'Change ZIP' };
  }

  /*
   * One place decides what the card looks like.
   *
   *   state    'unknown'     no window is in the cart — no ZIP, not asked yet, a
   *                          date known but not booked, or the last ask failed.
   *                          Selectable: choosing it books the soonest.
   *            'available'   a complete window is in the cart.
   *            'unavailable' Best Buy offered no dates for this ZIP. NOT
   *                          selectable — see below.
   *
   *   text     plain words before the link ('' for none) — only the transient and
   *            the failure states carry any.
   *   link     `{ text, aria }` for the link, or null to hide it (only while a
   *            lookup is in flight: there is nothing useful to press).
   *
   * ONE LINE, AND THE LINK IS IN IT (2026-10-19). Until then the line was text
   * and a separate "choose date" / "change date" / "Enter ZIP" button sat under
   * it — GP read that as being asked to choose a date. Now, in the steady states,
   * the line IS the link: the date when one is known, else the ZIP prompt — the
   * pickup card's shape ("Ready Tue, Oct 20 · FRISCO TX" + its link), with the
   * date where the store is.
   *
   * THE RADIO IS SELECTABLE WHENEVER THE CARD IS VISIBLE, except in
   * `unavailable`. It was "enabled only with a complete window in the cart" until
   * 2026-09-25, which is the forced-date behaviour GP asked us to drop — and the
   * reason a delivery-only appliance with no pickup could end up with nothing
   * selected in the group at all.
   *
   * `unavailable` stays disabled because it is the one state where selecting could
   * not produce a placeable order: Best Buy has no window in the next 14 days for
   * this ZIP, so there is nothing for selectHomeDelivery() to book, and the
   * pickup card behaves the same way when its store list comes back empty. Its
   * line ends in "Change ZIP", and the card body opens the drawer too, so another
   * ZIP is one click away.
   */
  function paintCard(state, text, link) {
    var card = cardEl();
    if (!card) return;
    var trigger = triggerEl(card);           // cache it before the slot is cleared
    var slot = card.querySelector('[data-im-home-delivery-state]');
    var radio = card.querySelector('input[type="radio"]');

    card.setAttribute('data-state', state);
    if (radio) {
      var selectable = !card.hidden && state !== 'unavailable';
      radio.disabled = !selectable;
      /*
       * It may UNCHECK but never CHECK, and now only when the control has stopped
       * being one. Selecting is an explicit act — the shopper's `change`, or
       * Liquid rendering the card checked from the cart — and a repaint that also
       * checked would steal the selection back from Store Pickup every time
       * something else redrew the card. Leaving a checked-and-selectable radio
       * alone is what lets the card stay selected through the "checking…" paint
       * between the shopper's click and the window landing.
       */
      if (!selectable && radio.checked) {
        radio.checked = false;
        ensureSelection();
      } else if (selectable && !card.closest('[data-im-fulfillment]').querySelector('input[type="radio"]:checked')) {
        /*
         * And the other way (2026-10-19): a card coming back from `unavailable`
         * — a new ZIP with dates — on a page where it is the only option left
         * the group empty, because `unavailable` unchecked it and nothing put it
         * back. Fill an EMPTY group only, through the same fallback order; a
         * script-made selection fires no `change`, so this asks Best Buy nothing.
         * Found in the preview QA of this change.
         */
        ensureSelection();
      }
    }
    if (!slot) return;
    slot.textContent = text ? text + (link ? ' ' : '') : '';
    if (!trigger) return;
    if (link && link.text) {
      trigger.textContent = link.text;
      if (link.aria) trigger.setAttribute('aria-label', link.aria);
      else trigger.removeAttribute('aria-label');
      trigger.hidden = false;
    } else {
      trigger.removeAttribute('aria-label');
      trigger.hidden = true;
    }
    // The text write above detaches it (Liquid renders it inside the line), so it
    // goes back in every time — after the words, as the end of the sentence.
    slot.appendChild(trigger);
  }

  /*
   * Repaint from what is already known — no network.
   *
   * In order, the first that applies:
   *
   *   a window in the cart          the date link, worded by who chose it
   *   the last answer for this      "As soon as <date>" link — or, if that
   *     SKU + ZIP (`answered`)        answer had no eligible window, "No delivery
   *                                   dates in the next 14 days" + Change ZIP
   *   the options answer's date     "As soon as <date>" link
   *   a ZIP                         "See the earliest date for 75034" link
   *   nothing                       "Enter a ZIP for the earliest date" link
   *
   * The delivery endpoint's own answer outranks the options answer's date: it is
   * the one placement claims with (`earliest`, delivery-eta.ts), and the two are
   * floored by the same lead time server-side, so they only differ when Best
   * Buy's lists moved between the two reads.
   *
   * The last two state no date, because none is known, and finding one out costs
   * a Best Buy call only a shopper may trigger. That is the one gap left in GP's
   * "if we have a zip, auto-pick the earliest date": a ZIP remembered from an
   * earlier page, on a product whose options answer carries no `earliestDate`.
   */
  function repaintCard() {
    var chosen = cartWindow();
    // A passed window states no date here (windowPassed); the card reads as if
    // the cart held none, from this product's own answers.
    if (chosen && !windowPassed(chosen)) {
      paintCard('available', '', dateLink(windowLabelText(chosen, chosen.earliest)));
      return;
    }
    var zip = getZip();
    var sku = currentSku();
    var known = (zip && sku && answered[sku + ':' + zip]) || null;
    if (known && known.none) {
      paintCard('unavailable', label('none', 'No delivery dates in the next 14 days'), changeZipLink());
      return;
    }
    /*
     * An earliest DATE we already hold — bestbuy.com's card ("Delivery — As soon
     * as Sat, Sep 26"), with no shopper action and no extra Best Buy call when it
     * came on the options answer.
     *
     * *This said "nothing sends it yet".* /apps/bestbuy/options sends
     * `delivery.earliestDate` as of 2026-09-25, forwarded from the
     * `<deliverydate>` list in the productservice answer it was already fetching
     * (availability.ts → earliestDeliveryDate) — when productservice lists any.
     * On 2026-10-19 the live answer for sku 6196700 at 75034 carried none.
     *
     * It is a date only: a date with no start time is not a placeable choice, so
     * the window still comes from a fresh lookup when the shopper selects the
     * card or presses Add to Cart. The state stays `unknown` for exactly that
     * reason.
     */
    var date = (zip && ((known && known.date) || offeredDate())) || '';
    if (date) {
      paintCard('unknown', '', dateLink(asSoonAsText(date)));
      return;
    }
    if (zip) { paintCard('unknown', '', lookLink(zip)); return; }
    paintCard('unknown', '', promptLink());
  }

  /* The line while a lookup is in flight: words, and no link to press. */
  function paintChecking(zip) {
    paintCard('unknown', label('checking', 'Checking delivery dates near') + ' ' + zip + '…', null);
  }

  /*
   * Take the card away for good (this session). Called for the three answers
   * that mean "there is nothing here to offer": the owner switch is off, the SKU
   * is not home-delivery, or the SKU is not one we list.
   *
   * If the cart was on home delivery, hand it back to Ground Shipping on the way
   * out — otherwise the shopper is left holding a choice with no control left on
   * the page to change it, and an order the backend would refuse.
   */
  function hideCard() {
    var card = cardEl();
    if (cartWindow()) releaseToShipping();
    if (!card) return;
    var radio = card.querySelector('input[type="radio"]');
    if (radio) { radio.checked = false; radio.disabled = true; }
    card.hidden = true;

    /*
     * Put the selection back on a control the shopper can still see.
     *
     * ensureSelection() rather than "check Ground Shipping" — which is what this
     * did until 2026-09-24. Unchecking the line above is what can empty the
     * group, so only an empty group needs filling; forcing shipping also moved a
     * shopper who was on Store Pickup, which this card's removal has nothing to
     * say about.
     */
    ensureSelection();
    announceCardShown();
  }

  /*
   * Put the selection back on a control the shopper can still see.
   *
   * A radio group with nothing checked is not a state anybody can read, and it is
   * also not a state the CART can be in — an absent `bb_fulfillment` is
   * ship-to-home to the backend, so showing nothing selected while the order
   * would ship is a lie of omission. Ground Shipping first because that is the
   * backend's own fallback; Store Pickup next; Home Delivery last.
   *
   * HOME DELIVERY IS A FALLBACK AT ALL only since 2026-09-25, and it is the whole
   * fix for the empty group. On a delivery-only appliance with no pickup,
   * improvemint-pickup.js disables the middle card's radio (§3a) and pickup is
   * unavailable, so before this the only remaining radio was one that stayed
   * disabled until a window was picked — and the shopper was left with NOTHING
   * selected. It is last because it is the only one of the three that needs a ZIP
   * and a Best Buy answer before the cart can carry it.
   *
   * Never fires a change event (assigning .checked does not), so it cannot bounce
   * through either file's change listener. It therefore does not WRITE the cart,
   * and MUST NOT: the change event is the only thing allowed to spend a Best Buy
   * lookup, and a script-made selection that fetched would put a delivery call on
   * every appliance page view (F5). So landing here can leave the radio showing
   * home delivery while the cart is still ship-to-home for one shopper action —
   * the card says "Enter a ZIP for the earliest date" in exactly that state, and
   * such an appliance is unplaceable either way until a window exists. Since
   * 2026-10-17 Add to Cart is one of those shopper actions: it books the soonest
   * window (bookSoonest, called from improvemint-pickup.js) or does not add —
   * it no longer clears the choice and lets the item through. Callers
   * that need the cart moved call releaseToShipping() as well, and the ones that
   * hide an unoffered card do exactly that when the cart was on home delivery.
   *
   * A HIDDEN OR DISABLED OPTION IS NEVER THE SELECTION (2026-10-02). Since GP's
   * "only show available shipping options", improvemint-pickup.js hides Store
   * Pickup and the shipping card whenever a real answer does not offer them, so a
   * card can leave the page while its radio is still checked. Such a radio is
   * unchecked here and the group refilled from the live ones; every candidate is
   * tested for `hidden` as well as `disabled`. When nothing is live the group is
   * left empty — that is the `no-option` state, and improvemint-pickup.js says so
   * and blocks Add to Cart; it does not ask this function to pretend otherwise.
   */
  function liveRadio(option) {
    var root = rootEl();
    var card = root && root.querySelector('[data-im-fulfillment-option="' + option + '"]');
    var radio = card && !card.hidden ? card.querySelector('input[type="radio"]') : null;
    return radio && !radio.disabled ? radio : null;
  }

  function ensureSelection() {
    var root = rootEl();
    if (!root) return;
    var checked = root.querySelector('input[type="radio"]:checked');
    if (checked) {
      var host = checked.closest('[data-im-fulfillment-option]');
      if (!checked.disabled && !(host && host.hidden)) return;
      checked.checked = false;
    }
    var pick = liveRadio('delivery') || liveRadio('store-pickup') || liveRadio('home-delivery');
    if (pick) pick.checked = true;
  }

  /*
   * Say whether the card is on the page — on the fieldset, and as an event —
   * every time this file shows or hides it (2026-10-02).
   *
   * improvemint-pickup.js decides whether ANY option is left and blocks Add to
   * Cart when none is. It can read the answer's flags, but not this file's
   * session memory: a session that already heard `disabled`, or a SKU already
   * refused, keeps this card hidden whatever the answer offers. And the warm-cache
   * race (improvemint-pickup.js, announceHomeDeliveryOffer) means its paint can
   * finish before this file has run at all. So the attribute is what it believes
   * once present, and the event is what makes it look again. Dispatched only on a
   * change, so a repaint that changes nothing costs nothing.
   */
  function announceCardShown() {
    var root = rootEl();
    var card = cardEl();
    if (!root || !card) return;
    var shown = card.hidden ? '0' : '1';
    if (root.getAttribute('data-im-home-delivery-shown') === shown) return;
    root.setAttribute('data-im-home-delivery-shown', shown);
    document.dispatchEvent(new CustomEvent('im:home-delivery-card', {
      detail: { shown: shown === '1' }
    }));
  }

  /*
   * Show or hide the card from the OPTIONS answer — the gate, added 2026-09-24.
   *
   * Liquid renders the card `hidden`, and this is the only thing that reveals it.
   * improvemint-pickup.js dispatches `im:home-delivery-offer` with
   * `delivery.homeDelivery && delivery.datePickerEnabled` from the fetch the PDP
   * already makes on load, so no Best Buy call was added for this gate — the
   * shopper-initiated call sites at the top of this file are still the only ones.
   *
   * WHY IT WAS NEEDED. Until this, the card rendered on every Best Buy PDP in
   * `unknown` and waited for a shopper to click it before it could learn the
   * owner switch was off — and it may only learn that by asking, and may only ask
   * when asked. So with `homeDeliveryEnabled` off, every appliance page carried a
   * dead "Home Delivery / Enter a ZIP to check dates / choose date" card that did
   * nothing but disappear when pressed (GP, live preview of
   * `upcoming-2026-09-24`).
   *
   * Three things this deliberately does NOT do:
   *
   *   It does not hide a card whose window is in the cart — WHEN THIS PRODUCT IS
   *   HOME-DELIVERED and the window's day is still ahead. Liquid renders it
   *   visible and checked in that state, and nothing here changes that.
   *   *Narrowed 2026-10-29: this kept the card for ANY cart window on ANY
   *   product, reasoning that the card was the only control that could change
   *   the choice. On a product Best Buy only ships, the Pickup and Shipping
   *   cards change it just as well, and the card offered that product a route
   *   that does not exist — GP saw "Home Delivery · Fri, Oct 23" on a 50" TV,
   *   six days after the date (commerce traced it: the options answer for 6668636
   *   says homeDelivery false). So a not-offered product now hides the card
   *   WITHOUT writing the cart (below), and a passed window reads as no window
   *   (windowPassed).*
   *
   *   It does not mark the session switched-off. That flag means "the endpoint
   *   told us `disabled`", and this answer is a different question asked of a
   *   different endpoint. Conflating them would suppress the card for the session
   *   on the strength of one SKU's flags.
   *
   *   It does not SELECT the card. Revealing an option is not choosing it, and
   *   Ground Shipping keeps the selection wherever it is still a truthful control.
   *   *Corrected 2026-09-25:* this said it "does not enable the radio", because
   *   selectable meant "a complete window is chosen". It does now — repaintCard()
   *   enables it, since a visible card the shopper cannot select is the forced-date
   *   behaviour GP asked us to drop — and ensureSelection() below can now land on
   *   it when the two cards beside it have gone dead.
   */
  function offerCard(offered) {
    var card = cardEl();
    if (!card) return;
    var sku = currentSku();
    var held = cartWindow();

    if (held && offered && !windowPassed(held)) { card.hidden = false; announceCardShown(); return; }

    if (offered) {
      // A session that already heard `disabled`, or a SKU already refused, stays
      // hidden whatever the options answer claims: those came from the endpoint
      // that actually owns the windows, so they are the later, better word. Still
      // fill the group on the way out — the two cards beside this one may have gone
      // dead while this card was the one being refused — and say it stayed hidden,
      // because improvemint-pickup.js may have counted it as offered.
      if (switchedOff() || (sku && skuRefused(sku))) { ensureSelection(); announceCardShown(); return; }
      card.hidden = false;
      repaintCard();
      /*
       * The delivery-only appliance, and the reason this line is here. By now
       * improvemint-pickup.js has hidden the middle card, disabled its radio and
       * unchecked it (§3a), and pickup is unavailable — so the group is empty at
       * this exact moment, in the same task, with nothing painted in between. This
       * is the only place that can fill it, because this is the first moment this
       * card is a selectable control.
       */
      ensureSelection();
      announceCardShown();
      return;
    }

    var radio = card.querySelector('input[type="radio"]');
    if (radio) { radio.checked = false; radio.disabled = true; }
    card.hidden = true;

    /*
     * An INCOMPLETE home-delivery cart (bb_fulfillment set, no usable window) is
     * the one state the backend fails rather than ships
     * (DELIVERY_WINDOW_MISSING), and cartWindow() reads null for it. Hand it back
     * to Ground Shipping before the control goes away.
     *
     * A COMPLETE window is left alone (2026-10-29): it was booked for another
     * line, and this product not being home-delivered says nothing about that
     * line. The card goes; the cart does not change until the shopper picks one
     * of the cards left here.
     */
    var root = rootEl();
    if (!held && root && root.getAttribute('data-im-cart-fulfillment') === 'home-delivery') releaseToShipping();
    ensureSelection();
    announceCardShown();
  }

  document.addEventListener('im:home-delivery-offer', function (e) {
    var detail = e.detail || {};
    setOfferedDate(detail.earliestDate);
    offerCard(!!detail.offered);
  });

  /* ----------------------------------------------------------------- drawer */

  var origin = null;                          // what opened it, for focus return

  /*
   * True when the drawer was opened BY A SELECTION that had no ZIP to work with —
   * so the ZIP the shopper is about to type is the answer to "when can you get it
   * here", not "show me a list". That submit books the earliest window and closes.
   * Cleared on every close, so the next open starts from whatever opened it.
   */
  var drawerAutoPick = false;

  /*
   * Which of the drawer's two modes to open in (2026-10-19) — see
   * snippets/improvemint-delivery-modal.liquid, "TWO MODES".
   *
   *   'zip'    no ZIP yet, or the card says this ZIP has no dates (its "Change ZIP"
   *            link). The drawer asks for the ZIP and nothing else, and the
   *            submit puts the earliest date on the card (peekEarliest) — GP:
   *            "if we don't have a zip, ask for zip, then lets auto-pick the
   *            earliest date". No list, no "choose a date".
   *   'dates'  a ZIP is known: the date link, or the card body once selected.
   *            Lists Morning / Afternoon / All day per day so the shopper can
   *            pick another.
   */
  function drawerModeFor(card) {
    if (!getZip()) return 'zip';
    return card && card.getAttribute('data-state') === 'unavailable' ? 'zip' : 'dates';
  }

  function openDrawer(from, autoPick, mode) {
    var m = modalEl();
    if (!m) return;
    origin = from || null;
    drawerAutoPick = !!autoPick;
    // Never 'dates' without a ZIP: there is nothing to list.
    var drawerMode = mode === 'zip' || !getZip() ? 'zip' : 'dates';
    m.setAttribute('data-mode', drawerMode);
    m.hidden = false;
    m.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('im-modal-open');

    var input = m.querySelector('[name="zip"]');
    if (input) {
      input.value = getZip() || '';
      // setTimeout, not rAF — the document-level click handlers that steal focus
      // elsewhere in this theme run inside this same dispatch. A macrotask runs
      // after the whole dispatch, so this focus survives. (Same reason as the
      // store drawer; it was measured there.)
      setTimeout(function () { input.focus(); input.select(); }, 0);
    }
    // ZIP mode asks for the ZIP and fetches nothing until it is submitted; the
    // heading already says what to do, so the results line stays empty.
    if (drawerMode === 'zip') { showResults(''); return; }
    // A ZIP the shopper already gave is a ZIP they already asked us about, and
    // opening the drawer IS the ask — the first of the five allowed calls. A
    // drawer the shopper opened themselves lists the dates; one a selection
    // opened books the soonest, if a ZIP is somehow already in hand.
    loadWindows(drawerAutoPick);
  }

  function closeDrawer() {
    var m = modalEl();
    if (!m) return;
    m.hidden = true;
    m.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('im-modal-open');
    drawerAutoPick = false;
    /*
     * Focus goes back to what opened the drawer — the card's link, normally,
     * which by now reads the date. If that link is hidden (a lookup still in
     * flight, see paintChecking) focus goes to the card's radio instead, so it is
     * never dropped on <body>.
     */
    var back = origin;
    if (back && (back.hidden || !back.isConnected)) {
      var card = cardEl();
      back = card && !card.hidden ? card.querySelector('input[type="radio"]:not([disabled])') : null;
    }
    if (back && back.focus) back.focus();
    origin = null;
  }

  function resultsEl() {
    var m = modalEl();
    return m && m.querySelector('[data-im-delivery-results]');
  }

  function showResults(text) {
    var r = resultsEl();
    if (!r) return;
    r.removeAttribute('data-loading');
    r.textContent = text;
  }

  /*
   * Render the choice: one heading per day, and under it AT MOST THREE buttons,
   * Morning, Afternoon and All day (2026-10-19).
   *
   * GP: "Lets group times to morning or afternoon, and only show that options per
   * day." *Until 2026-10-19 this listed every Best Buy window as its clock range
   * ("8:00 AM – 12:00 PM", "12:00 PM – 4:00 PM", …).* Now each day's windows are
   * split by dayPart() — ends by 13:00 Morning, starts from 12:00 Afternoon,
   * anything else All day, shown with its hours ("All day (7 AM – 7 PM)") — and a
   * group with no window that day is simply not offered. Each button
   * carries ONE real window, the EARLIEST-STARTING window in its group, because
   * placement needs a complete Best Buy window (date + start), not a part of day:
   * pickWindow() writes that window's exact date, start and end, as before. The
   * earliest start, because it is the one most likely to still be on offer when
   * placement re-reads Best Buy (F15) and the one a shopper who said "morning"
   * would expect.
   *
   * ONLY `eligibleWindows` (offerableWindows, 2026-10-17) — never the raw
   * `windows`, which include days our home-delivery lead time forbids. This
   * drawer is the "change date" affordance docs/COMMERCE-PDP-PICKER.md §9 tells to
   * render that list; until 2026-10-17 it rendered `windows`.
   *
   * The shopper's current choice is marked with `aria-current` on the part of day
   * that holds it (same day, same part, same ZIP) — which is also what draws it.
   *
   * These are real <button>s, where the store drawer's tiles had to be divs with
   * role="button" — a <button> cannot legally contain another button, and each
   * store tile holds an "Add to cart" button. A part-of-day tile holds nothing, so
   * it gets to be the real element and inherits keyboard activation, focus and
   * the disabled semantics for free instead of re-implementing them. Its spoken
   * name is the day and the part — "Thu, Oct 23 · Morning", exactly what the card
   * and the cart will say once it is chosen — because "Morning" alone, read out of
   * its group, does not say which day.
   */
  function renderWindows(data, zip, notice) {
    var r = resultsEl();
    if (!r) return;
    r.removeAttribute('data-loading');
    r.textContent = '';

    if (notice) {
      var p = document.createElement('p');
      p.className = 'im-pickup__error';
      p.textContent = notice;
      r.appendChild(p);
    }

    var chosen = cartWindow();
    var chosenPart = chosen && chosen.zip === zip ? dayPart(chosen) : '';
    var byDate = [];
    var index = {};                           // date → { morning, afternoon, allday: window }
    offerableWindows(data).forEach(function (w) {
      if (!w || !DATE_RE.test(w.date || '') || !TIME_RE.test(w.startTime || '')) return;  // never half a window
      var part = dayPart(w);
      if (!index[w.date]) { index[w.date] = {}; byDate.push(w.date); }
      var held = index[w.date][part];
      if (!held || w.startTime < held.startTime) index[w.date][part] = w;
    });
    // ISO dates sort as strings. The endpoint sends them sorted; this does not
    // rely on it, for the reason earliestWindow() gives.
    byDate.sort();

    byDate.forEach(function (date) {
      var group = document.createElement('div');
      group.className = 'im-delivery__day';

      var heading = document.createElement('p');
      heading.className = 'im-delivery__day-title';
      heading.textContent = dayLabel(date);
      group.appendChild(heading);

      var list = document.createElement('div');
      list.className = 'im-delivery__slots';

      ['morning', 'afternoon', 'allday'].forEach(function (part) {
        var w = index[date][part];
        if (!w) return;                       // no window in that part of the day
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'im-delivery__slot';
        btn.setAttribute('data-im-window-pick', '');
        btn.setAttribute('data-date', w.date);
        btn.setAttribute('data-start', w.startTime);
        btn.setAttribute('data-end', w.endTime || '');
        btn.textContent = dayPartText(w);
        btn.setAttribute('aria-label', windowLabelText(w, false));
        if (chosenPart === part && chosen.date === date) btn.setAttribute('aria-current', 'true');
        list.appendChild(btn);
      });

      group.appendChild(list);
      r.appendChild(group);
    });
  }

  /* Date + start time are what the order actually carries, and what placement
   * matches on; the end time is display and logs only. */
  function offered(windows, chosen) {
    for (var i = 0; i < windows.length; i++) {
      if (windows[i].date === chosen.date && windows[i].startTime === chosen.startTime) return true;
    }
    return false;
  }

  /*
   * The drawer's lookup. Reached from three shopper actions and nothing else:
   * opening the drawer in dates mode, submitting a ZIP into it (or into a ZIP-mode
   * drawer a selection opened), and selecting the Home Delivery radio
   * (2026-09-25). See "CALL IT ONLY WHEN THE SHOPPER ASKS" at the top for why the
   * third one clears the same bar as the other two. (The other lookups —
   * peekEarliest, bookSoonest, soonestWindow — are a ZIP submit, Add to Cart and
   * the cart; none is reachable from a page view.)
   *
   * `autoPick` is what the third one turns on: book the EARLIEST window without
   * asking, rather than render a list and wait. It is also set on the ZIP the
   * shopper submits into a drawer that a selection opened, because that ZIP is the
   * answer to a question the selection asked.
   *
   * If a window cannot be booked — no dates, a failure, a busy queue — an
   * autoPick selection is REVERTED rather than left standing: the radio would
   * otherwise read "Home Delivery" over a cart that is still ship-to-home, which
   * is the one thing the cart banner and the picker must never do
   * (improvemint-cart-fulfillment.liquid).
   */
  function loadWindows(autoPick) {
    var sku = currentSku();
    var zip = getZip();
    var r = resultsEl();
    if (!sku || !zip) {
      showResults(label('prompt', 'Enter a ZIP for the earliest date'));
      if (autoPick) revertSelection();
      return;
    }
    if (switchedOff() || skuRefused(sku)) { hideCard(); closeDrawer(); return; }

    if (r) {
      r.setAttribute('data-loading', '');
      r.textContent = label('checking', 'Checking delivery dates near') + ' ' + zip + '…';
    }
    // The card says so too: with the drawer closed (a radio selection) the results
    // list is the one thing the shopper cannot see.
    if (autoPick) paintChecking(zip);

    lookup(sku, zip).then(function (data) {
      if (data.error) {
        handleError(data.error, sku);
        if (autoPick) revertSelection();
        return;
      }

      /*
       * Is the window the shopper already chose still on offer?
       *
       * Best Buy holds nothing (F15) — a window can be taken while the shopper
       * is still on the page. Placement re-reads it per SKU before claiming the
       * order for exactly this reason (deliveryWindowOffered in pipeline.ts);
       * this is the same test, done at the only other moment we have a fresh
       * list in hand, so the page stops showing a date we can no longer honour
       * instead of carrying it all the way to a failed order.
       *
       * Only when the answer is for the SAME ZIP the window was read for. A list
       * for another ZIP says nothing about it, and treating that as "gone" would
       * throw away a perfectly good choice.
       *
       * Against the windows we may OFFER (2026-10-17): a window still on Best
       * Buy's list but now inside our lead time is gone for us too.
       */
      var offerable = offerableWindows(data);
      var chosen = cartWindow();
      var lost = false;
      if (chosen && chosen.zip === zip && !offered(offerable, chosen)) {
        releaseToShipping();
        chosen = null;
        lost = true;
      }

      if (!offerable.length) {
        // Documented as "Best Buy has no dates in the next 14 days" — not "this
        // product cannot be delivered". Say so; do not hide the product. Since
        // 2026-10-17 it is also the answer when Best Buy listed windows but our
        // lead time is past all of them (`eligibleWindows: []`, `earliest:
        // null`): the same words, because to the shopper it is the same fact.
        // paintCard('unavailable') also disables the radio and hands the selection
        // on, which is the revert for an autoPick that found nothing. Its line ends
        // in "Change ZIP" (2026-10-19), which reopens this drawer asking for one.
        paintCard('unavailable', label('none', 'No delivery dates in the next 14 days'), changeZipLink());
        showResults(label('none', 'No delivery dates in the next 14 days') + ' for ' + zip + '.');
        return;
      }

      /*
       * The no-forced-date path. The shopper asked for home delivery, so take the
       * soonest window Best Buy offered and write it — one complete, placeable
       * choice, from a shopper action that named no date.
       *
       * Not when a window is already chosen: that one is the shopper's (or was
       * ours and has survived the `lost` check above), and quietly moving it to an
       * earlier date would overwrite a choice nobody asked us to revisit.
       */
      if (autoPick && !chosen) {
        var soonest = earliestWindow(data);
        if (soonest) {
          commitWindow(soonest, zip, true);
          selectCard();
          repaintCard();
          // The drawer is only open here because the selection had no ZIP to work
          // with and opened it to ask. The question is answered; close it rather
          // than leave a date list in the shopper's face, which is the very thing
          // this change exists to stop doing.
          closeDrawer();
          return;
        }
        // Windows arrived but none of them was usable (no date, no start time).
        // Same outcome as none at all: never write half a window.
        paintCard('unavailable', label('none', 'No delivery dates in the next 14 days'), changeZipLink());
        showResults(label('none', 'No delivery dates in the next 14 days') + ' for ' + zip + '.');
        return;
      }

      renderWindows(data, zip, lost ? label('lost', 'That delivery window has been taken. Please choose another.') : '');
      /*
       * Nothing is selected by arriving here: the card only turns `available` once
       * a window is in the cart. But this answer's earliest date is now known
       * (lookup → noteAnswer), so the card behind the drawer states it — "As soon
       * as Thu, Oct 23" — instead of the "Delivery dates available near <zip>" it
       * said until 2026-10-19. No extra call: it is the answer just fetched.
       */
      repaintCard();
    });
  }

  /* Check this card's radio without firing `change` — so it cannot bounce back
   * into the listener below and ask for a second lookup. */
  function selectCard() {
    var card = cardEl();
    var radio = card && card.querySelector('input[type="radio"]');
    if (!radio) return;
    radio.disabled = false;
    radio.checked = true;
  }

  /*
   * The shopper chose Home Delivery. Answer it without asking for a date.
   *
   * Three ways it can go, and the order matters:
   *
   *   a window is already in the cart  re-assert it (it is the shopper's own, or
   *                                    ours from earlier) and say nothing new.
   *                                    A `change` can fire on a card Liquid
   *                                    already rendered checked, and re-writing
   *                                    is cheaper than trusting the cart survived
   *                                    a checkout or a second tab.
   *   a ZIP is known                   ask Best Buy and book the SOONEST window.
   *                                    The lookup is the third allowed call site
   *                                    (see the header) — a shopper act on one
   *                                    product, cached 60 s per SKU+ZIP.
   *   no ZIP                           invite one, exactly as the pickup card
   *                                    does, and undo the selection until it
   *                                    arrives. We cannot write a home-delivery
   *                                    cart without a window, and we cannot get a
   *                                    window without a ZIP — so leaving the radio
   *                                    checked would show a choice the cart does
   *                                    not hold. The drawer opens in ZIP mode
   *                                    (asking for the ZIP, never for a date —
   *                                    2026-10-19), and its submit finishes the
   *                                    job (drawerAutoPick): it books the soonest
   *                                    window and closes.
   */
  function selectHomeDelivery() {
    var chosen = cartWindow();
    if (chosen) {
      commitWindow(chosen, chosen.zip || getZip() || '', chosen.earliest);
      repaintCard();
      return;
    }
    if (!getZip()) {
      revertSelection();
      openDrawer(triggerEl(cardEl()) || cardEl(), true, 'zip');
      return;
    }
    loadWindows(true);
  }

  /*
   * Undo a selection we could not honour, and never leave the group empty doing
   * it. Only touches the radio when the cart does NOT hold a window: with one in
   * hand the selection is real and stays.
   */
  function revertSelection() {
    if (cartWindow()) return;
    var card = cardEl();
    var radio = card && card.querySelector('input[type="radio"]');
    if (radio && radio.checked) radio.checked = false;
    ensureSelection();
  }

  /*
   * Each error, and what it is allowed to change.
   *
   * Nothing here ever enables the radio or shows a date. The two "there is
   * nothing to offer" answers remove the card for the session; everything else
   * leaves it disabled with an explanation, so a retry is one click away and the
   * server-rendered picker is untouched.
   */
  function handleError(code, sku) {
    if (code === 'disabled') {
      markSwitchedOff();
      hideCard();
      closeDrawer();
      return;
    }
    if (code === 'not-home-delivery' || code === 'invalid-sku') {
      markSkuRefused(sku);
      hideCard();
      closeDrawer();
      return;
    }
    // The ZIP passed our own 5-digit test and the server still refused it, so it is
    // a ZIP the server will not accept — ask again rather than retry. It paints the
    // CARD as well as the drawer (2026-09-25): a selection-triggered lookup runs
    // with the drawer shut, so the card would otherwise be left reading
    // "Checking delivery dates near …" with nothing coming.
    //
    // With no window held, the failure IS the card's link (2026-10-19): "Couldn't
    // load delivery dates — try again shortly", pressed, reopens the drawer, which
    // asks again — the retry is the first allowed call, the shopper opening it.
    var text = errorText(code);
    var held = cartWindow();
    if (held) paintCard('available', '', dateLink(windowLabelText(held, held.earliest)));
    else paintCard('unknown', '', text ? { text: text } : null);
    showResults(text);
  }

  /* The words for a failed lookup that leaves the card standing. The two answers
   * that remove the card (`disabled`, `not-home-delivery` / `invalid-sku`) have no
   * words here: improvemint-pickup.js says what is left once the card has gone. */
  function errorText(code) {
    if (code === 'disabled' || code === 'not-home-delivery' || code === 'invalid-sku') return '';
    return code === 'invalid-zip'
      ? label('zip-error', 'Enter a 5-digit ZIP code.')
      : code === 'busy'
      ? label('busy', 'Delivery dates are busy — try again in a minute')
      : label('error', 'Couldn’t load delivery dates — try again shortly');
  }

  /*
   * Book the soonest window for the SKU on this page, for ADD TO CART
   * (2026-10-17) — the fifth caller in the header.
   *
   * improvemint-pickup.js calls this from its capture-phase add listener when Home
   * Delivery is the selection and the cart holds no complete window — the state
   * ensureSelection() leaves on every delivery-only appliance with no pickup. It
   * used to clear the fulfilment instead, and the item went to checkout as
   * ship-to-home, which placement fails for a truck-only SKU.
   *
   * Resolves `{ ok: true }` only once a complete window is IN THE CART, so the
   * caller can let the add go; otherwise `{ ok: false, error, text }`, and the add
   * must not happen. Never rejects.
   *
   *   window already in the cart  ok — nothing to do (the caller checks too).
   *   no ZIP                      no add. The drawer opens in ZIP mode asking for
   *                               one, as the radio's own no-ZIP path does, and its
   *                               submit books the soonest window (drawerAutoPick).
   *                               The shopper presses Add to Cart again once the
   *                               card reads a date.
   *   a ZIP                       one lookup, the soonest window written exactly as
   *                               selecting the card writes it — all eleven keys,
   *                               `bb_delivery_earliest: "1"` (commitWindow).
   *   no window offered           no add, and the card says "No delivery dates in
   *                               the next 14 days", the existing copy.
   *   a failure / busy            no add, and the card says so (handleError).
   *   switch off / SKU refused    no add; the card goes (handleError → hideCard),
   *                               and improvemint-pickup.js's `no-option` block
   *                               says what is left.
   *
   * The SKU and ZIP are re-read when the answer lands: a variant switch or a ZIP
   * change mid-flight makes the answer somebody else's, and writing it would book
   * a window for the wrong appliance or address.
   */
  function bookSoonest() {
    var card = cardEl();
    var sku = currentSku();
    if (!card || card.hidden || !sku) return Promise.resolve({ ok: false, error: 'no-card', text: '' });
    if (cartWindow()) return Promise.resolve({ ok: true });
    if (switchedOff() || skuRefused(sku)) {
      hideCard();
      return Promise.resolve({ ok: false, error: 'disabled', text: '' });
    }

    var zip = getZip();
    if (!zip) {
      openDrawer(triggerEl(card) || card, true, 'zip');
      return Promise.resolve({ ok: false, error: 'invalid-zip', text: label('prompt', 'Enter a ZIP for the earliest date') });
    }

    paintChecking(zip);

    return lookup(sku, zip).then(function (data) {
      if (currentSku() !== sku || getZip() !== zip) {
        repaintCard();
        return { ok: false, error: 'stale', text: '' };
      }
      if (data.error) {
        handleError(data.error, sku);
        return { ok: false, error: data.error, text: errorText(data.error) };
      }
      var soonest = earliestWindow(data);
      if (!soonest) {
        // No dates, or none usable (no date / no start time): never half a window.
        var none = label('none', 'No delivery dates in the next 14 days');
        paintCard('unavailable', none, changeZipLink());
        return { ok: false, error: 'none', text: none };
      }
      return commitWindow(soonest, zip, true).then(function (r) {
        if (!r || !r.ok) {
          // The cart did not take it. Say the mirror holds nothing, so the next
          // press asks again rather than trusting a window that never landed.
          rememberCart('', null);
          repaintCard();
          return { ok: false, error: 'cart', text: label('error', 'Couldn’t load delivery dates — try again shortly') };
        }
        selectCard();
        repaintCard();
        return { ok: true };
      });
    }).catch(function () {
      repaintCard();
      return { ok: false, error: 'unavailable', text: label('error', 'Couldn’t load delivery dates — try again shortly') };
    });
  }

  // Beside soonestWindow, for the same reason: the caller needs a promise back.
  window.imDelivery.bookSoonest = bookSoonest;

  /* ----------------------------------------------------------------- wiring */

  function pickWindow(btn) {
    var zip = getZip();
    if (!zip) { openDrawer(btn); return; }
    var win = {
      date: btn.getAttribute('data-date'),
      startTime: btn.getAttribute('data-start'),
      endTime: btn.getAttribute('data-end') || ''
    };
    if (!win.date || !win.startTime) return;  // never write half a window

    /*
     * The button carries ONE real window — the earliest-starting one in the part
     * of day it names (renderWindows) — so this writes a complete date + start
     * pair exactly as a clock-window pick did before 2026-10-19.
     *
     * `earliest: false` — the shopper reached into the drawer and named this day,
     * so every surface shows "Thu, Oct 23 · Morning" rather than "As soon as …".
     * It is false even when they happen to have clicked the soonest one: "as soon
     * as" describes OUR choice of the earliest on their behalf, and they did not
     * ask us to choose.
     */
    commitWindow(win, zip, false);
    repaintCard();
    // Programmatic: assigning .checked fires no change event, so this cannot
    // bounce back through the change listener below (or pickup.js's).
    selectCard();
    closeDrawer();
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    // The card's link — the date, or the ZIP prompt (2026-10-19). Opens the
    // drawer asking for a ZIP when there is none (or the card says this ZIP has
    // no dates), else listing Morning / Afternoon / All day per day.
    var trigger = e.target.closest('[data-im-delivery-zip]');
    if (trigger) {
      e.preventDefault();
      openDrawer(trigger, false, drawerModeFor(trigger.closest('[data-im-fulfillment-option="home-delivery"]')));
      return;
    }

    var slot = e.target.closest('[data-im-window-pick]');
    if (slot) { e.preventDefault(); pickWindow(slot); return; }

    var closer = e.target.closest('#im-delivery-modal [data-im-delivery-close]');
    if (closer) { e.preventDefault(); closeDrawer(); return; }

    /*
     * The card body: SELECT while unselected, OPEN THE DRAWER once selected.
     *
     * A click on an unselected, selectable card means "deliver it to me", and the
     * `change` listener below answers it by booking the soonest window — popping
     * the date list on top of that is the forced choice GP asked us to remove on
     * 2026-09-25, which is why the card body stopped opening the drawer then.
     *
     * Once the card IS the selection, a click on it fires no `change` (the radio
     * is already checked) and so did nothing at all — on every delivery-only
     * appliance, where ensureSelection() checks this card on load. Since
     * 2026-10-19 it opens the drawer, the way a click on the selected Store Pickup
     * card opens the store list (GP: "similar to how we show the store in store
     * pickup card and they click in and they can select another store"). Opening
     * the drawer is the first of the five allowed calls; a click is a shopper act.
     *
     * A DISABLED radio fires no `change` either, and that card opens the drawer as
     * it always has: `unavailable` means "Best Buy has no dates for THIS ZIP",
     * precisely the answer a shopper would want to retry from another one, so it
     * opens asking for a ZIP. (When there is genuinely nothing behind the card —
     * the switch is off, or the SKU is not deliverable — it is removed outright
     * rather than disabled.)
     *
     * "Already selected" is read on the LABEL's click, before the browser forwards
     * it to the radio and checks it; the forwarded click (target: the radio) is
     * ignored, and so is a click straight on the radio, which is a selection. The
     * macrotask lets that second dispatch settle first, and the `hidden` test
     * makes both passes idempotent.
     */
    var card = e.target.closest('[data-im-fulfillment-option="home-delivery"]');
    if (card) {
      var cardRadio = card.querySelector('input[type="radio"]');
      if (!cardRadio || e.target === cardRadio) return;
      if (!cardRadio.disabled && !cardRadio.checked) return;   // a selection: `change` answers it
      setTimeout(function () {
        var m = modalEl();
        if (m && m.hidden) openDrawer(triggerEl(card) || card, false, drawerModeFor(card));
      }, 0);
    }
  });

  document.addEventListener('keydown', function (e) {
    var m = modalEl();
    if (e.key === 'Escape' && m && !m.hidden) closeDrawer();
  });

  /*
   * A ZIP submitted into a ZIP-mode drawer the shopper opened from the card's
   * link (2026-10-19): put the earliest date on the card and close. GP: "if we
   * don't have a zip, ask for zip, then lets auto-pick the earliest date + the
   * configurable lead days".
   *
   * One lookup, and it is not a new call site: it is "the shopper submits a ZIP",
   * the second of the five, which until this date listed the windows instead. It
   * READS the date and BOOKS NOTHING. A ZIP is where the shopper is, not a choice
   * of fulfilment, and writing the cart here would throw away whatever the cart
   * already carries (a pickup chosen one product ago) on an act that chose
   * nothing — the same reason the picker never reconciles on page load (§6). The
   * window is booked when the shopper selects the card or presses Add to Cart,
   * from a fresh read, exactly as before. (A ZIP-mode drawer that a SELECTION
   * opened books, through loadWindows(true) — that shopper did choose.)
   *
   * The date is the endpoint's `earliest` — Best Buy's earliest window + our
   * home-delivery lead time, computed server-side; the theme adds none. It
   * reaches the card through noteAnswer() → repaintCard(). No date (`earliest:
   * null`) leaves the drawer open saying so, with the card reading "No delivery
   * dates in the next 14 days · Change ZIP"; a failure leaves it open with the
   * failure, as the dates list does.
   */
  function peekEarliest() {
    var sku = currentSku();
    var zip = getZip();
    if (!sku || !zip) { showResults(''); return; }
    if (switchedOff() || skuRefused(sku)) { hideCard(); closeDrawer(); return; }

    var r = resultsEl();
    if (r) {
      r.setAttribute('data-loading', '');
      r.textContent = label('checking', 'Checking delivery dates near') + ' ' + zip + '…';
    }
    paintChecking(zip);

    lookup(sku, zip).then(function (data) {
      // A variant switch or another ZIP mid-flight: this answer is somebody
      // else's. Its date is already filed under its own SKU + ZIP.
      if (currentSku() !== sku || getZip() !== zip) { repaintCard(); return; }
      if (data.error) { handleError(data.error, sku); return; }

      // The same "is the chosen window still on offer" test the list makes.
      var chosen = cartWindow();
      if (chosen && chosen.zip === zip && !offered(offerableWindows(data), chosen)) releaseToShipping();

      repaintCard();
      if (!earliestWindow(data)) {
        showResults(label('none', 'No delivery dates in the next 14 days') + ' for ' + zip + '.');
        return;
      }
      showResults('');
      closeDrawer();
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-im-delivery-zip-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('[name="zip"]');
    var err = form.querySelector('[data-im-delivery-zip-error]');
    var value = ((input && input.value) || '').trim();

    if (!/^\d{5}$/.test(value)) {
      if (err) err.hidden = false;
      if (input) input.focus();
      return;
    }
    if (err) err.hidden = true;

    var changed = value !== getZip();
    setZip(value);
    if (changed) {
      onZipChanged();
      // Tell improvemint-pickup.js, which owns the other two cards and the
      // product-card callouts, that the ZIP moved under it.
      document.dispatchEvent(new CustomEvent('im:zip-change', { detail: { zip: value, source: 'delivery' } }));
    }
    // `drawerAutoPick` when a selection opened this drawer to ask for the ZIP: the
    // shopper asked for home delivery, not for a list, so book the soonest window
    // and close. A ZIP-mode drawer the shopper opened from the card's link puts
    // the earliest date on the card and closes (2026-10-19). A dates-mode drawer
    // lists Morning / Afternoon / All day for the new ZIP.
    var m = modalEl();
    if (drawerAutoPick) loadWindows(true);
    else if (m && m.getAttribute('data-mode') === 'zip') peekEarliest();
    else loadWindows(false);
  });

  /*
   * A ZIP change invalidates a chosen window, full stop.
   *
   * The window was offered for the OLD ZIP, and placement refuses an order whose
   * window was read for a different ZIP than it ships to (DELIVERY_ZIP_MISMATCH).
   * Keeping `bb_fulfillment: home-delivery` with the date blanked would be worse
   * still — that is the incomplete choice the backend fails rather than ships.
   * So the whole choice goes back to Ground Shipping and the shopper picks again
   * from the new ZIP's dates, which the submit is about to look up (loadWindows,
   * or peekEarliest from a ZIP-mode drawer).
   */
  function onZipChanged() {
    if (!cartWindow()) return;
    releaseToShipping();
    repaintCard();
  }

  document.addEventListener('im:zip-change', function (e) {
    if (e.detail && e.detail.source === 'delivery') return;   // our own
    onZipChanged();
    // NOT a lookup: the shopper asked the OTHER drawer a question. Calling here
    // would be a delivery lookup nobody requested.
    repaintCard();
  });

  document.addEventListener('change', function (e) {
    var radio = e.target.closest && e.target.closest('[data-im-fulfillment] input[type="radio"]');
    if (!radio || !radio.checked) return;

    if (radio.value === 'home-delivery') { selectHomeDelivery(); return; }
    /*
     * The shopper moved to pickup or shipping. improvemint-pickup.js does the
     * write — including the empty bb_delivery_* keys — AND moves the fieldset's
     * cart mirror with it (its rememberCart), in its own `change` listener, which
     * runs before this one. So this file only repaints.
     *
     * It used to write the mirror here too, as `store-pickup` for any pickup
     * selection. That was a claim about a cart write this file never saw, and for
     * Pickup with no store remembered there IS no write — improvemint-pickup.js
     * opens the store drawer instead. The mirror then said the cart was on pickup
     * when it was not (and blanked a home-delivery window the cart still held),
     * which is what made the store drawer's dismissal revert work only sometimes
     * (2026-10-01; see leaveStorelessPickup in improvemint-pickup.js).
     */
    repaintCard();
  });

  function init() {
    var card = cardEl();
    if (!card) return;
    var sku = currentSku();
    // The switch was already answered for this session, or this SKU was already
    // refused — take the card away before it is ever offered, with no call.
    if (switchedOff() || (sku && skuRefused(sku))) { hideCard(); return; }

    /*
     * The cart holds a window but this browser has no ZIP — a second device, or
     * cleared site data. Adopt the cart's ZIP so the drawer opens on the dates
     * for the address the window was actually read for. Only when there is no
     * ZIP already: overwriting one would move the pickup UI's ZIP too, and that
     * is the shopper's, not ours.
     */
    var chosen = cartWindow();
    if (!getZip() && chosen && /^\d{5}$/.test(chosen.zip || '')) setZip(chosen.zip);

    // Liquid printed the cart's window, and it cannot know the day has gone by.
    // Take the passed date off now rather than when the options answer lands.
    if (chosen && windowPassed(chosen) && cardEl() && !cardEl().hidden) repaintCard();

    /*
     * READ THE OFFER OFF THE FIELDSET, because the event may already have been and
     * gone (2026-09-25).
     *
     * improvemint-pickup.js is the deferred script BEFORE this one, and on a warm
     * options cache its answer resolves in the microtask checkpoint at the end of
     * its own script — so `im:home-delivery-offer` can be dispatched before the
     * listener above exists, and this card would then never reveal for the rest of
     * the page view. The full measurement is in that file, at
     * announceHomeDeliveryOffer, which writes these two attributes for exactly this
     * reason.
     *
     * An ABSENT attribute means no answer has arrived yet — leave the
     * server-rendered state alone and wait for the event, as before.
     */
    var root = rootEl();
    var offer = root && root.getAttribute('data-im-home-delivery-offer');
    if (offer === '1' || offer === '0') {
      setOfferedDate(root.getAttribute('data-im-home-delivery-earliest') || '');
      offerCard(offer === '1');
      return;                                    // offerCard() repaints
    }

    repaintCard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The PDP section re-renders on a variant change and on theme-editor edits.
  document.addEventListener('shopify:section:load', init);
})();
